import { format, isSameDay } from "date-fns";
import {
  Clock,
  User,
  CheckCircle2,
  Circle,
  MoreVertical,
  Bell,
  X,
  AlertTriangle,
  CalendarDays,
  CalendarIcon,
  RotateCcw,
  Square,
  CheckSquare,
  ExternalLink,
  CalendarCheck,
  Phone,
  ListChecks,
  AlarmClock,
  Cake,
  FileText,
} from "lucide-react";
import React, { useState, useRef, useCallback } from "react";
import { useRouter } from "next/navigation";

import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { Checkbox } from "@/app/_module/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import {
  Dialog,
  ResponsiveDialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Calendar } from "@/app/_module/components/ui/calendar";
import { Label } from "@/app/_module/components/ui/label";
import { Input } from "@/app/_module/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/app/_module/components/ui/popover";
import { cn } from "@/app/_module/lib/utils";
import type { ReminderUI } from "@/app/_module/types/reminders";

// Status includes 'notified' - notification sent, user has 10 min grace period before overdue
type ReminderStatus = "pending" | "notified" | "completed" | "cancelled" | "overdue";

interface ReminderCardProps {
  reminder: ReminderUI;
  onComplete: (id: string) => void;
  onEdit: (reminder: ReminderUI) => void;
  onDelete: (id: string) => void;
  onStatusChange?: (id: string, status: ReminderStatus) => void;
  onPriorityChange?: (id: string, priority: "urgent" | "high" | "medium" | "low") => void;
  onReschedule?: (id: string, newDate: Date, newTime: Date, reminder: ReminderUI) => void;
  isLoading?: boolean;
  // Selection mode props
  selectionMode?: boolean;
  isSelected?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
}

export const ReminderCard = function({
  reminder,
  onComplete,
  onEdit,
  onDelete,
  onStatusChange,
  onPriorityChange,
  onReschedule,
  isLoading = false,
  selectionMode = false,
  isSelected = false,
  onSelect,
}: ReminderCardProps) {
  const router = useRouter();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [lastActionTime, setLastActionTime] = useState(0);
  const [isRescheduleOpen, setIsRescheduleOpen] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState<Date | undefined>(
    reminder.dueDate ? (typeof reminder.dueDate === 'string' ? new Date(reminder.dueDate) : reminder.dueDate) : undefined
  );
  const [rescheduleTime, setRescheduleTime] = useState<string>(
    reminder.reminderTime ? format(reminder.reminderTime, "HH:mm") : "09:00"
  );
  const timeListRef = useRef<HTMLDivElement>(null);

  // Get lead ID from associations or associatedClientId
  const getLeadId = useCallback(() => {
    // First check associations array for lead type
    const leadAssociation = reminder.associations?.find(
      (assoc) => assoc.entityType?.toLowerCase() === "lead"
    );
    if (leadAssociation?.entityId) return leadAssociation.entityId;
    // Fall back to associatedClientId from UI metadata
    if (reminder.associatedClientId) return reminder.associatedClientId;
    return null;
  }, [reminder.associations, reminder.associatedClientId]);

  const leadId = getLeadId();

  // Navigate to lead details page
  const handleNavigateToLead = useCallback(() => {
    if (leadId) {
      router.push(`/leads/${leadId}`);
    }
  }, [leadId, router]);

  // Get current time slot value for scrolling
  const getCurrentTimeSlotValue = useCallback(() => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = Math.floor(now.getMinutes() / 15) * 15;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  }, []);

  // Scroll to selected time or current time when popover opens
  const scrollToCurrentTime = useCallback(() => {
    // Use multiple attempts with increasing delays to ensure popover is rendered
    const attemptScroll = (attempt = 0) => {
      if (attempt > 5) return; // Max 5 attempts

      setTimeout(() => {
        const container = timeListRef.current;
        if (container && container.scrollHeight > container.clientHeight) {
          // First try to scroll to the selected time, otherwise scroll to current time
          const targetTime = rescheduleTime || getCurrentTimeSlotValue();
          const targetButton = container.querySelector(`[data-time="${targetTime}"]`) as HTMLElement;

          const scrollToButton = (button: HTMLElement) => {
            // Calculate position relative to container and scroll manually
            // This avoids scrollIntoView affecting parent containers
            const buttonRect = button.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();
            const relativeTop = buttonRect.top - containerRect.top + container.scrollTop;
            const centerOffset = (container.clientHeight - button.clientHeight) / 2;
            container.scrollTop = relativeTop - centerOffset;
          };

          if (targetButton) {
            scrollToButton(targetButton);
          } else {
            // Fallback to current time slot
            const currentSlotValue = getCurrentTimeSlotValue();
            const fallbackButton = container.querySelector(`[data-time="${currentSlotValue}"]`) as HTMLElement;
            if (fallbackButton) {
              scrollToButton(fallbackButton);
            }
          }
        } else if (attempt < 5) {
          // Container not ready or not scrollable yet, try again
          attemptScroll(attempt + 1);
        }
      }, 50 + (attempt * 50)); // Start at 50ms, increase by 50ms each attempt
    };

    attemptScroll(0);
  }, [getCurrentTimeSlotValue, rescheduleTime]);

  // Generate time slots (every 15 minutes) with disabled state for past times
  const generateTimeSlots = useCallback(() => {
    const slots: { value: string; label: string; disabled: boolean }[] = [];
    const now = new Date();
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const todayStr = format(now, "yyyy-MM-dd");
    const selectedDateStr = rescheduleDate ? format(rescheduleDate, "yyyy-MM-dd") : "";
    const isTodaySelected = selectedDateStr === todayStr;

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourStr = String(hour).padStart(2, '0');
        const minStr = String(minute).padStart(2, '0');
        const value = `${hourStr}:${minStr}`;
        const hour12 = hour % 12 || 12;
        const ampm = hour < 12 ? 'AM' : 'PM';
        const label = `${hour12}:${minStr} ${ampm}`;

        // Disable past times only if today is selected
        const disabled = isTodaySelected && value < currentTime;

        slots.push({ value, label, disabled });
      }
    }
    return slots;
  }, [rescheduleDate]);

  // Format time to 12-hour display
  const formatTimeDisplay = (timeStr: string) => {
    if (!timeStr) return 'Select time';
    const [hours, minutes] = timeStr.split(':').map(Number);
    const hour12 = hours % 12 || 12;
    const ampm = hours < 12 ? 'AM' : 'PM';
    return `${hour12}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };
  
  // Debounce actions to prevent rapid clicks
  const debounceAction = (action: () => void, delay = 1000) => {
    const now = Date.now();
    if (now - lastActionTime < delay) {
      return; // Too soon, ignore the action
    }
    setLastActionTime(now);
    action();
  };
  
  // Trust the backend status completely - no client-side overdue derivation
  // Backend handles: pending -> notified (when notification sent) -> overdue (after 10 min grace period)
  const derivedStatus: ReminderStatus = (() => {
    // Trust explicit backend status first
    if (reminder.status === "cancelled") return "cancelled";
    if (reminder.status === "completed" || reminder.isCompleted) return "completed";
    if (reminder.status === "overdue") return "overdue";
    if (reminder.status === "notified") return "notified";
    // Default to pending - let backend handle overdue transition
    return "pending";
  })();

  // Only 'overdue' should show as overdue (red styling)
  // 'notified' is a separate state (orange styling) - notification sent, awaiting action
  const isOverdue = derivedStatus === "overdue";
  const isDueToday = isSameDay(reminder.dueDate, new Date());
  const isCompleted = derivedStatus === "completed";

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case "urgent":
        return "bg-red-100 text-red-800 border-red-200";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "medium":
        return "bg-blue-100 text-blue-800 border-blue-200";
      case "low":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusColor = (status: ReminderStatus) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "notified":
        // Notified = notification sent, user has 10 min grace period - show as orange/warning
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status: ReminderStatus) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "cancelled":
        return <X className="h-5 w-5 text-red-600" />;
      case "overdue":
        return <AlertTriangle className="h-5 w-5 text-red-600" />;
      case "notified":
        // Notified = notification sent, awaiting action - show bell icon
        return <Bell className="h-5 w-5 text-orange-600" />;
      case "pending":
        return <Circle className="h-5 w-5 text-gray-400" />;
      default:
        return <Circle className="h-5 w-5 text-gray-400" />;
    }
  };

  // Lucide SVG icons per reminder type — replaces the old iOS-emoji set so
  // the UI looks the same on every OS/font stack (iOS emoji renders badly
  // on Windows/Linux where the system emoji font is different).
  const getTypeIcon = (type: string) => {
    const iconProps = { className: "h-4 w-4" };
    switch (type) {
      case "appointment":
        return <CalendarCheck {...iconProps} />;
      case "follow-up":
        return <Phone {...iconProps} />;
      case "task":
        return <ListChecks {...iconProps} />;
      case "deadline":
        return <AlarmClock {...iconProps} />;
      case "birthday":
        return <Cake {...iconProps} />;
      default:
        return <FileText {...iconProps} />;
    }
  };

  return (
    <div
      className={cn(
        "bg-white rounded-lg border p-4 space-y-3 transition-all hover:shadow-md",
        isCompleted && "opacity-60",
        isOverdue && "border-red-200 bg-red-50",
        isDueToday && !isOverdue && "border-orange-200 bg-orange-50",
        isSelected && "ring-2 ring-blue-500 bg-blue-50"
      )}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div className="flex items-center gap-2">
            {/* Show checkbox in selection mode only */}
            {selectionMode && (
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => onSelect?.(reminder.id, !!checked)}
                className="h-5 w-5"
              />
            )}

            {/* Reschedule button for cancelled reminders */}
            {!selectionMode && derivedStatus === "cancelled" && (onReschedule || onStatusChange) && (
              <Button
                className="h-6 px-2 text-xs text-blue-600 hover:text-blue-700"
                disabled={isLoading || isUpdating}
                onClick={() => {
                  if (onReschedule) {
                    setRescheduleDate(reminder.dueDate ? (typeof reminder.dueDate === 'string' ? new Date(reminder.dueDate) : reminder.dueDate) : undefined);
                    setRescheduleTime(reminder.reminderTime ? format(reminder.reminderTime, "HH:mm") : "09:00");
                    setIsRescheduleOpen(true);
                  } else if (onStatusChange) {
                    debounceAction(() => {
                      setIsUpdating(true);
                      onStatusChange(reminder.id, "pending");
                      setTimeout(() => setIsUpdating(false), 2000);
                    });
                  }
                }}
                size="sm"
                variant="outline"
              >
                <CalendarDays className="h-3 w-3 mr-1" />
                Reschedule
              </Button>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="inline-flex items-center text-muted-foreground">
                {getTypeIcon(reminder.type)}
              </span>
              <h3
                className={cn(
                  "font-medium text-gray-900",
                  isCompleted && "line-through text-gray-500"
                )}
              >
                {reminder.title}
              </h3>
            </div>

            {!!reminder.description && (
              <p className="text-sm text-gray-600 mb-2">
                {reminder.description}
              </p>
            )}

            <div className="flex items-center gap-4 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{format(reminder.dueDate, "MMM d")}</span>
              </div>

              {!!reminder.reminderTime && (
                <div className="flex items-center gap-1">
                  <Bell className="h-4 w-4" />
                  <span>
                    {format(reminder.reminderTime, "h:mm a")}
                  </span>
                </div>
              )}

              {/* Status Badge */}
              <Badge className={cn(getStatusColor(derivedStatus), "text-xs")}>
                {derivedStatus.replace("-", " ")}
              </Badge>
            </div>

            {!!reminder.associatedClientName && (
              <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                <User className="h-4 w-4" />
                {leadId ? (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNavigateToLead();
                    }}
                    className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                  >
                    {reminder.associatedClientName}
                    <ExternalLink className="h-3 w-3" />
                  </button>
                ) : (
                  <span>{reminder.associatedClientName}</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Only Priority Badge remains in the top right */}
          <Badge className={getPriorityColor(reminder.priority)}>
            {reminder.priority}
          </Badge>

          <DropdownMenu open={isMenuOpen} onOpenChange={setIsMenuOpen}>
            <DropdownMenuTrigger asChild>
              <Button 
                className="h-8 w-8 p-0" 
                disabled={isLoading || isUpdating} 
                size="sm"
                variant="ghost"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* View Lead - only show if reminder has an associated lead */}
              {!!leadId && (
                <>
                  <DropdownMenuItem
                    onClick={() => {
                      handleNavigateToLead();
                      setIsMenuOpen(false);
                    }}
                  >
                    <User className="h-4 w-4 mr-2" />
                    View Lead
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}

              <DropdownMenuItem
                onClick={() => {
                  onEdit(reminder);
                  setIsMenuOpen(false);
                }}
              >
                Edit
              </DropdownMenuItem>

              {/* Status Change Submenu */}
              {!!onStatusChange && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Circle className="h-4 w-4 mr-2" />
                    Change Status
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onStatusChange) return;
                        debounceAction(() => {
                          setIsUpdating(true);
                          onStatusChange(reminder.id, "pending");
                          setIsMenuOpen(false);
                          setTimeout(() => setIsUpdating(false), 2000);
                        });
                      }}
                    >
                      <Circle className="h-4 w-4 mr-2 text-yellow-600" />
                      Pending
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onStatusChange) return;
                        debounceAction(() => {
                          setIsUpdating(true);
                          onStatusChange(reminder.id, "completed");
                          setIsMenuOpen(false);
                          setTimeout(() => setIsUpdating(false), 2000);
                        });
                      }}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2 text-green-600" />
                      Completed
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onStatusChange) return;
                        setIsUpdating(true);
                        onStatusChange(reminder.id, "cancelled");
                        setIsMenuOpen(false);
                        setTimeout(() => setIsUpdating(false), 1000);
                      }}
                    >
                      <X className="h-4 w-4 mr-2 text-red-600" />
                      Cancelled
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onStatusChange) return;
                        setIsUpdating(true);
                        onStatusChange(reminder.id, "overdue");
                        setIsMenuOpen(false);
                        setTimeout(() => setIsUpdating(false), 1000);
                      }}
                    >
                      <AlertTriangle className="h-4 w-4 mr-2 text-red-600" />
                      Overdue
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              {/* Priority Change Submenu */}
              {!!onPriorityChange && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <AlertTriangle className="h-4 w-4 mr-2" />
                    Change Priority
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onPriorityChange) return;
                        debounceAction(() => {
                          setIsUpdating(true);
                          onPriorityChange(reminder.id, "urgent");
                          setIsMenuOpen(false);
                          setTimeout(() => setIsUpdating(false), 2000);
                        });
                      }}
                    >
                      <div className="h-2 w-2 rounded-full bg-red-500 mr-2" />
                      Urgent
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onPriorityChange) return;
                        setIsUpdating(true);
                        onPriorityChange(reminder.id, "high");
                        setIsMenuOpen(false);
                        setTimeout(() => setIsUpdating(false), 1000);
                      }}
                    >
                      <div className="h-2 w-2 rounded-full bg-orange-500 mr-2" />
                      High
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onPriorityChange) return;
                        setIsUpdating(true);
                        onPriorityChange(reminder.id, "medium");
                        setIsMenuOpen(false);
                        setTimeout(() => setIsUpdating(false), 1000);
                      }}
                    >
                      <div className="h-2 w-2 rounded-full bg-yellow-500 mr-2" />
                      Medium
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={isUpdating}
                      onClick={() => {
                        if (isUpdating || !onPriorityChange) return;
                        setIsUpdating(true);
                        onPriorityChange(reminder.id, "low");
                        setIsMenuOpen(false);
                        setTimeout(() => setIsUpdating(false), 1000);
                      }}
                    >
                      <div className="h-2 w-2 rounded-full bg-green-500 mr-2" />
                      Low
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}

              <DropdownMenuSeparator />
              
              <DropdownMenuItem
                className="text-red-600"
                disabled={isLoading || isUpdating}
                onClick={() => {
                  onDelete(reminder.id);
                  setIsMenuOpen(false);
                }}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {!!reminder.notes && (
        <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded">
          <strong>Notes:</strong> {reminder.notes}
        </div>
      )}

      {/* Reschedule Dialog */}
      <Dialog open={isRescheduleOpen} onOpenChange={setIsRescheduleOpen}>
        <ResponsiveDialogContent size="md">
          <DialogHeader>
            <DialogTitle>Reschedule Reminder</DialogTitle>
            <DialogDescription>
              Choose a new date and time for this reminder.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            {/* Date Picker with Calendar Popover */}
            <div className="space-y-2">
              <Label htmlFor="reschedule-date">Date</Label>
              <Popover>
                <div className="relative">
                  <Input
                    id="reschedule-date"
                    type="date"
                    value={rescheduleDate ? format(rescheduleDate, "yyyy-MM-dd") : ""}
                    onChange={(e) => {
                      if (e.target.value) {
                        const [year, month, day] = e.target.value.split("-").map(Number);
                        setRescheduleDate(new Date(year, month - 1, day, 12, 0, 0));
                      } else {
                        setRescheduleDate(undefined);
                      }
                    }}
                    className="w-full pr-10"
                    min={format(new Date(), "yyyy-MM-dd")}
                  />
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                    >
                      <CalendarIcon className="h-4 w-4 text-gray-400" />
                    </Button>
                  </PopoverTrigger>
                </div>
                <PopoverContent align="start" className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={rescheduleDate}
                    onSelect={(date) => {
                      if (date) {
                        const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);
                        setRescheduleDate(localDate);
                      }
                    }}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Time Picker with Popover Time Slots */}
            <div className="space-y-2">
              <Label>Time</Label>
              <Popover onOpenChange={(open) => {
                if (open) scrollToCurrentTime();
              }}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !rescheduleTime && "text-muted-foreground"
                    )}
                  >
                    {formatTimeDisplay(rescheduleTime)}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0 z-[60]" align="start">
                  {/* Manual time input at top */}
                  <div className="p-3 border-b">
                    <p className="text-xs text-muted-foreground mb-2">Type custom time:</p>
                    <Input
                      type="time"
                      value={rescheduleTime}
                      onChange={(e) => setRescheduleTime(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  {/* Quick select 15-min intervals */}
                  <div className="p-2">
                    <p className="text-xs text-muted-foreground mb-2 px-1">Or select:</p>
                    <div
                      ref={timeListRef}
                      className="grid grid-cols-4 gap-1 max-h-[200px] overflow-y-auto"
                      onWheel={(e) => e.stopPropagation()}
                    >
                      {generateTimeSlots().map((slot) => (
                        <Button
                          key={slot.value}
                          type="button"
                          data-time={slot.value}
                          variant={rescheduleTime === slot.value ? "default" : "ghost"}
                          size="sm"
                          disabled={slot.disabled}
                          className={cn(
                            "text-xs h-8",
                            slot.disabled && "text-muted-foreground line-through opacity-50",
                            rescheduleTime === slot.value && "bg-primary text-primary-foreground"
                          )}
                          onClick={() => setRescheduleTime(slot.value)}
                        >
                          {slot.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsRescheduleOpen(false)}
            >
              Cancel
            </Button>
            <Button
              disabled={!rescheduleDate || isUpdating}
              onClick={() => {
                if (!rescheduleDate || !onReschedule) return;
                setIsUpdating(true);
                const [hours, minutes] = rescheduleTime.split(":").map(Number);
                const newTime = new Date();
                newTime.setHours(hours, minutes, 0, 0);
                onReschedule(reminder.id, rescheduleDate, newTime, reminder);
                setIsRescheduleOpen(false);
                setTimeout(() => setIsUpdating(false), 2000);
              }}
            >
              <CalendarDays className="h-4 w-4 mr-2" />
              Reschedule
            </Button>
          </DialogFooter>
        </ResponsiveDialogContent>
      </Dialog>
    </div>
  );
}